// Trades-per-day bars over the last 60 days. Anti-FOMO surface: red bars
// over the max-daily-trades rule threshold.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SectionCard } from './ui/SectionCard';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { Activity } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';

const DAYS = 60;

export function TradeFrequencyChart() {
  const orders = useQuery({
    queryKey: ['history-orders-large'],
    queryFn: () => api.orderHistory(),
  });
  const rules = useQuery({ queryKey: ['rules'], queryFn: api.rules });

  const cap = useMemo(() => {
    const r = (rules.data ?? []).find((x) => x.id === 'max-daily-trades');
    const v = r?.params.max;
    return typeof v === 'number' ? v : 5;
  }, [rules.data]);

  const data = useMemo(() => {
    const map = new Map<string, number>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = DAYS - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      map.set(d.toISOString().slice(0, 10), 0);
    }
    for (const o of orders.data?.items ?? []) {
      const day = o.createdAt.slice(0, 10);
      if (map.has(day)) map.set(day, (map.get(day) ?? 0) + 1);
    }
    return [...map.entries()].map(([date, count]) => ({ date, count }));
  }, [orders.data]);

  const overCap = data.filter((d) => d.count > cap).length;

  return (
    <SectionCard>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity className="size-4 text-indigo-300" />
          <h3 className="text-sm font-medium text-zinc-200">Trade frequency</h3>
        </div>
        <p className="text-[11px] text-zinc-400">
          Last {DAYS} days · cap {cap}/day · {overCap} day{overCap === 1 ? '' : 's'} over cap
        </p>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
            <XAxis
              dataKey="date"
              tickFormatter={(v) => fmtDate(v).slice(0, 6)}
              minTickGap={40}
              tick={{ fill: '#71717a', fontSize: 11 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={{ stroke: '#27272a' }}
            />
            <YAxis
              tick={{ fill: '#71717a', fontSize: 11 }}
              axisLine={{ stroke: '#27272a' }}
              tickLine={{ stroke: '#27272a' }}
              width={28}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                background: '#0a0a0f',
                border: '1px solid #26262f',
                borderRadius: 8,
                fontSize: 11,
              }}
              labelStyle={{ color: '#a1a1aa' }}
              itemStyle={{ color: '#e4e4e7' }}
              labelFormatter={(v) => fmtDate(String(v))}
              formatter={(v: number) => [`${v} trade${v === 1 ? '' : 's'}`, '']}
            />
            <ReferenceLine
              y={cap}
              stroke="#f59e0b"
              strokeDasharray="3 3"
              strokeOpacity={0.7}
            />
            <Bar dataKey="count" isAnimationActive={false}>
              {data.map((d, i) => (
                <Cell
                  key={i}
                  fill={d.count > cap ? '#fb7185' : d.count > 0 ? '#818cf8' : 'rgba(63, 63, 70, 0.5)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
}
